import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ArrowRight } from 'lucide-react';
import { useDiwaliOffer } from '../hooks/useDiwaliOffer';

const KEY = 'dyssc_diwali_popup_seen';
const SHOW_DELAY = 2500;
const AUTO_CLOSE = 3000;
const GOLD = '#FFB547';
const RED = '#E02128';

const Diya = () => (
  <span className="relative inline-flex w-12 h-12 items-center justify-center" aria-hidden="true">
    <span className="absolute w-8 h-8 -top-1 rounded-full blur-xl opacity-90 animate-[diya-glow_1.8s_ease-in-out_infinite]" style={{ background: GOLD }} />
    <svg viewBox="0 0 48 48" className="relative w-12 h-12">
      <path d="M24 4c4.6 6 7 10.4 7 13.8a7 7 0 0 1-14 0C17 14.4 19.4 10 24 4z" fill={GOLD} className="origin-bottom animate-[diya-flicker_1.6s_ease-in-out_infinite]" />
      <path d="M24 12c1.8 2.4 2.6 4.1 2.6 5.5a2.6 2.6 0 0 1-5.2 0c0-1.4.8-3.1 2.6-5.5z" fill="#FFF3D6" />
      <path d="M5 28h38c-1.4 8-9.4 14-19 14S6.4 36 5 28z" fill={RED} />
      <path d="M5 28h38c-.2 1.5-.6 2.9-1.2 4.2H6.2C5.6 30.9 5.2 29.5 5 28z" fill={GOLD} />
    </svg>
  </span>
);

const Sparks = () => (
  <>
    {[[12, 18, 0], [84, 14, 0.4], [90, 70, 0.8], [8, 78, 1.2], [50, 8, 1.6]].map(([x, y, d], i) => (
      <span key={i} className="absolute w-1 h-1 rounded-full animate-[diya-glow_2s_ease-in-out_infinite]"
        style={{ left: `${x}%`, top: `${y}%`, background: GOLD, boxShadow: `0 0 8px ${GOLD}`, animationDelay: `${d}s` }} />
    ))}
  </>
);

export const DiwaliPopup = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const cfg = useDiwaliOffer();

  useEffect(() => {
    if (!cfg?.popup || localStorage.getItem(KEY)) return;
    const t = setTimeout(() => { setOpen(true); localStorage.setItem(KEY, '1'); }, SHOW_DELAY);
    return () => clearTimeout(t);
  }, [cfg]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => setOpen(false), AUTO_CLOSE);
    return () => clearTimeout(t);
  }, [open]);

  const shop = (e) => {
    e.stopPropagation();
    setOpen(false);
    const go = () => document.getElementById('shop')?.scrollIntoView({ behavior: 'smooth' });
    if (pathname === '/' || pathname === '/home') go(); else { navigate('/'); setTimeout(go, 600); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-4 pb-8 bg-black/30 backdrop-blur-[2px] cursor-pointer"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setOpen(false)}
          data-testid="diwali-popup-overlay"
        >
          <motion.div
            className="relative w-full max-w-[300px] p-px rounded-3xl overflow-hidden bg-[length:200%_100%] animate-[gradient-x_3s_linear_infinite]"
            style={{ backgroundImage: `linear-gradient(90deg, ${RED}, ${GOLD}, ${RED})`, boxShadow: `0 20px 60px ${RED}55, 0 0 40px ${GOLD}33` }}
            initial={{ y: 40, scale: 0.9, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }} exit={{ y: 20, scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 26 }}
            data-testid="diwali-popup"
          >
            <div className="relative rounded-3xl bg-[#0A0A0A] px-6 pt-6 pb-5 text-center overflow-hidden">
              <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: `radial-gradient(${GOLD} 1px, transparent 1px)`, backgroundSize: '14px 14px' }} />
              <Sparks />
              <button type="button" aria-label="Close" onClick={() => setOpen(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="diwali-popup-close">
                <X className="w-4 h-4" />
              </button>
              <div className="relative">
                <Diya />
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>Happy Diwali</p>
                <h3 className="mt-2 font-heading font-bold text-2xl text-white leading-tight" data-testid="diwali-popup-title">
                  New Diwali <span className="bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(90deg, ${GOLD}, #FF7A45)` }}>Prices</span>
                </h3>
                <p className="mt-1.5 text-xs text-zinc-400">Light up your game this festive season.</p>
                <button type="button" onClick={shop}
                  className="group mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-white transition-transform active:scale-95 hover:scale-[1.02]"
                  style={{ background: RED, boxShadow: `0 8px 24px ${RED}66` }}
                  data-testid="diwali-popup-shop-btn">
                  Shop Now <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
              <motion.span className="absolute left-0 bottom-0 h-[2px]" style={{ background: GOLD }}
                initial={{ width: '100%' }} animate={{ width: '0%' }} transition={{ duration: AUTO_CLOSE / 1000, ease: 'linear' }} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
